import { CompanyCountryExposureItem, CompanyRegionExposureItem } from '@api';
import {
  RevampTableRoot,
  TableCell,
  TableHeader,
} from '@design/components/table';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { FlagIcon } from '@terminal/components/flag-icon/FlagIcon';
import { RegionIcon } from '@terminal/components/region-icon/RegionIcon';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LoaderCell } from '../../theme-exposure-tab/theme-exposure-table/loader-cell/LoaderCell';
import { RankingMethodologySection } from '../../theme-exposure-tab/theme-exposure-table/ranking-methodology-section/RankingMethodologySection';
import { TopRankIcon } from '../../top-rank-icon/TopRankIcon';
import styles from './GeographicExposureTable.module.scss';

type CountryItem = CompanyCountryExposureItem;
type RegionItem = CompanyRegionExposureItem;

type GeographicExposureTableProps =
  | {
      mode: 'country';
      data?: CountryItem[];
      isFetching?: boolean;
      onRowClick: (code: string) => void;
    }
  | {
      mode: 'region';
      data?: RegionItem[];
      isFetching?: boolean;
      onRowClick: (name: string) => void;
    };

export const GeographicExposureTable = (
  props: GeographicExposureTableProps
) => {
  const { t } = useTranslation(['widget', 'countries']);

  const columns = useMemo<ColumnDef<CountryItem | RegionItem>[]>(() => {
    const nameHeader =
      props.mode === 'country'
        ? t('widget:assetOverview.knowledgeGraph.geographicExposure.country')
        : t('widget:assetOverview.knowledgeGraph.geographicExposure.region');

    return [
      {
        id: 'rank',
        header: t('widget:assetOverview.knowledgeGraph.themeExposure.rank'),
        cell: ctx => (
          <div className={styles.rankWrapper}>
            <TopRankIcon rank={ctx.row.original.relationship.rank} />
          </div>
        ),
      },
      {
        id: 'name',
        header: nameHeader,
        cell: ctx => {
          if (props.mode === 'country') {
            const row = ctx.row.original as CountryItem;
            return (
              <div className={styles.nameWrapper}>
                <FlagIcon
                  countryCode={row.code}
                  variant="circular"
                  customSize={32}
                />
                <span className={styles.nameLabel}>
                  {t(`countries:${row.code.toUpperCase()}`)}
                </span>
              </div>
            );
          }

          const row = ctx.row.original as RegionItem;
          return (
            <div className={styles.nameWrapper}>
              <RegionIcon region={row.name} size={32} />
              <span className={styles.nameLabel}>{row.name}</span>
            </div>
          );
        },
      },
      {
        id: 'description',
        header: t(
          'widget:assetOverview.knowledgeGraph.themeExposure.description'
        ),
        cell: ctx => (
          <div className={styles.descriptionWrapper}>
            {ctx.row.original.relationship.description}
          </div>
        ),
      },
    ];
  }, [t, props.mode]);

  const table = useReactTable({
    data: props.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  return (
    <div
      className={clsx(styles.tableWrapper, {
        [styles.loading]: !!props.isFetching,
        [styles.notLoading]: !props.isFetching,
      })}
    >
      <RevampTableRoot className={styles.styledRevampTableRoot}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHeader
                  key={header.id}
                  className={styles.styledTableHeader}
                  data-table-header
                  alignment="left"
                  isHoverEnabled={false}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHeader>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {props.isFetching ? (
            <LoaderCell numberOfRows={3} />
          ) : (
            rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    className={styles.styledTableCell}
                    data-table-cell
                    alignment="left"
                    isNumber={cell.column.id === 'rank'}
                    onClick={() =>
                      props.onRowClick(
                        props.mode === 'country'
                          ? (row.original as CountryItem).code
                          : (row.original as RegionItem).name
                      )
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </tr>
            ))
          )}
          <tr data-testid="footer-row">
            <TableCell
              className={styles.footerRow}
              data-footer-row
              colspan={table.getHeaderGroups()[0].headers.length}
              isNumber={false}
            >
              <RankingMethodologySection
                rankingType={'companyToGeographicExposure'}
              />
            </TableCell>
          </tr>
        </tbody>
      </RevampTableRoot>
    </div>
  );
};
